import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../context/ThemeContext";
import { mapIconButton } from "../../components/ui/mapChrome";
import { clampTo, isDefaultUnitDisplay } from "./unitDisplay";
import { useUnitDisplayStore } from "./unitDisplayStore";
import {
  LABEL_STYLES,
  NAME_MODES,
  UNIT_DISPLAY_RANGES,
  type LabelStyle,
  type NameMode,
  type RangeKey,
} from "./types";

const CLICK_TOLERANCE_PX = 5;

interface ToolTheme {
  button: string;
  panel: string;
  title: string;
  label: string;
  muted: string;
  chip: string;
  segTrack: string;
  segActive: string;
  segIdle: string;
  switchOn: string;
  switchOff: string;
  knobOn: string;
  knobOff: string;
  accent: string;
  divider: string;
  ghost: string;
}

const TOOL_THEMES = {
  dark: {
    button: "bg-dark-surface text-white ring-1 ring-white/20 hover:bg-dark-bg",
    panel: "bg-dark-surface border-dark-border text-white",
    title: "text-white",
    label: "text-gray-300",
    muted: "text-gray-500",
    chip: "bg-white/10 text-white",
    segTrack: "bg-black/30",
    segActive: "bg-white text-gray-900",
    segIdle: "text-gray-400 hover:bg-white/10 hover:text-white",
    switchOn: "bg-white",
    switchOff: "bg-white/25",
    knobOn: "bg-gray-900",
    knobOff: "bg-white",
    accent: "accent-white",
    divider: "border-white/10",
    ghost: "text-gray-200 hover:bg-white/10",
  },
  light: {
    button: "bg-white text-gray-700 ring-1 ring-black/10 hover:bg-gray-50",
    panel: "bg-white border-gray-200 text-gray-900",
    title: "text-gray-900",
    label: "text-gray-600",
    muted: "text-gray-500",
    chip: "bg-gray-100 text-gray-900",
    segTrack: "bg-gray-100",
    segActive: "bg-gray-900 text-white",
    segIdle: "text-gray-500 hover:bg-gray-200 hover:text-gray-900",
    switchOn: "bg-gray-900",
    switchOff: "bg-gray-300",
    knobOn: "bg-white",
    knobOff: "bg-white",
    accent: "accent-gray-900",
    divider: "border-gray-200",
    ghost: "text-gray-600 hover:bg-gray-100",
  },
} as const satisfies Record<"dark" | "light", ToolTheme>;

interface SliderRowProps {
  ui: ToolTheme;
  label: string;
  field: RangeKey;
  value: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}

function SliderRow({ ui, label, field, value, format, onChange }: SliderRowProps) {
  const { min, max, step } = UNIT_DISPLAY_RANGES[field];
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center justify-between text-[11px]">
        <span className={ui.label}>{label}</span>
        <span className={`rounded px-1.5 py-px tabular-nums ${ui.chip}`}>{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(clampTo(field, Number(event.target.value)))}
        className={`h-4 w-full cursor-pointer ${ui.accent}`}
      />
    </label>
  );
}

interface SegmentedProps<T extends string> {
  ui: ToolTheme;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

function Segmented<T extends string>({ ui, label, value, options, onChange }: SegmentedProps<T>) {
  return (
    <div className="flex flex-col gap-1">
      <span className={`text-[11px] ${ui.label}`}>{label}</span>
      <div role="group" aria-label={label} className={`flex gap-0.5 rounded-lg p-0.5 text-[11px] ${ui.segTrack}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={`flex-1 rounded-md px-1.5 py-1 font-medium transition-colors ${
              value === option.value ? `${ui.segActive} shadow` : ui.segIdle
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface SwitchRowProps {
  ui: ToolTheme;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function SwitchRow({ ui, label, checked, onChange }: SwitchRowProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`flex items-center justify-between text-[11px] ${ui.label}`}
    >
      <span>{label}</span>
      <span className={`relative h-4 w-8 rounded-full transition-colors ${checked ? ui.switchOn : ui.switchOff}`}>
        <span
          className={`absolute top-0.5 h-3 w-3 rounded-full shadow transition-all ${
            checked ? `left-[18px] ${ui.knobOn}` : `left-0.5 ${ui.knobOff}`
          }`}
        />
      </span>
    </button>
  );
}

function Section({ ui, icon, title, children }: { ui: ToolTheme; icon: string; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <h4 className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${ui.muted}`}>
        <i className={`${icon} text-xs`} aria-hidden />
        {title}
      </h4>
      {children}
    </section>
  );
}

export default function UnitDisplayTool() {
  const { t } = useTranslation("home");
  const { isDarkMode } = useDarkMode();
  const ui: ToolTheme = isDarkMode ? TOOL_THEMES.dark : TOOL_THEMES.light;
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const settings = useUnitDisplayStore((state) => state.settings);
  const update = useUnitDisplayStore((state) => state.update);
  const reset = useUnitDisplayStore((state) => state.reset);

  useEffect(() => {
    if (!open) return;
    const pressed = { x: 0, y: 0, outside: false };
    const isInside = (target: EventTarget | null) =>
      [buttonRef.current, panelRef.current].some((node) => node?.contains(target as Node));
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    const onDown = (event: PointerEvent) => {
      pressed.x = event.clientX;
      pressed.y = event.clientY;
      pressed.outside = !isInside(event.target);
    };
    const onUp = (event: PointerEvent) => {
      const moved = Math.hypot(event.clientX - pressed.x, event.clientY - pressed.y) > CLICK_TOLERANCE_PX;
      pressed.outside && !moved && setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointerup", onUp, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointerup", onUp, true);
    };
  }, [open]);

  const title = t("unitDisplay.title", "Visualización de unidades");
  const nameLabels: Record<NameMode, string> = {
    full: t("unitDisplay.nameMode.full", "Completo"),
    unit: t("unitDisplay.nameMode.unit", "Unidad"),
    none: t("unitDisplay.nameMode.none", "Quitar"),
  };
  const nameOptions = (Object.keys(NAME_MODES) as NameMode[]).map((value) => ({ value, label: nameLabels[value] }));
  const styleOptions = (Object.keys(LABEL_STYLES) as LabelStyle[]).map((value) => ({
    value,
    label: t(`unitDisplay.labelStyle.${value}`, value === "card" ? "Tarjeta" : "Halo"),
  }));
  const hasName = settings.name_mode !== "none";

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={title}
        aria-expanded={open}
        title={title}
        // Fila de arriba a la derecha (mapChrome): filtro, búsqueda, pantalla completa.
        className={`absolute top-3 right-[92px] z-20 ${mapIconButton(isDarkMode)} ${open ? "ring-2 ring-gray-400/50" : ""}`}
      >
        <i className="ri-equalizer-line text-[17px]" aria-hidden />
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={title}
          className={`absolute right-3 top-14 z-30 flex max-h-[calc(100%-5rem)] w-64 max-w-[calc(100%-1.5rem)] flex-col gap-3 overflow-y-auto rounded-xl border p-3 shadow-2xl motion-safe:animate-fadeIn ${ui.panel}`}
        >
          <div className="flex items-center justify-between">
            <h3 className={`flex items-center gap-1.5 text-xs font-semibold ${ui.title}`}>
              <i className={`ri-equalizer-line ${ui.muted}`} aria-hidden />
              {title}
            </h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("unitDisplay.close", "Cerrar")}
              className={`flex h-6 w-6 items-center justify-center rounded ${ui.muted} ${ui.ghost}`}
            >
              <i className="ri-close-line text-base" aria-hidden />
            </button>
          </div>

          <Section ui={ui} icon="ri-text" title={t("unitDisplay.sectionLabel", "Etiqueta")}>
            <Segmented<NameMode>
              ui={ui}
              label={t("unitDisplay.name", "Nombre")}
              value={settings.name_mode}
              options={nameOptions}
              onChange={(name_mode) => update({ name_mode })}
            />
            {hasName && (
              <SliderRow
                ui={ui}
                label={t("unitDisplay.labelSize", "Tamaño del nombre")}
                field="label_px"
                value={settings.label_px}
                format={(value) => `${value} px`}
                onChange={(label_px) => update({ label_px })}
              />
            )}
            <Segmented<LabelStyle>
              ui={ui}
              label={t("unitDisplay.style", "Estilo de la etiqueta")}
              value={settings.label_style}
              options={styleOptions}
              onChange={(label_style) => update({ label_style })}
            />
            {settings.label_style === "card" && (
              <SliderRow
                ui={ui}
                label={t("unitDisplay.cardOpacity", "Opacidad de la tarjeta")}
                field="card_opacity"
                value={settings.card_opacity}
                format={(value) => `${Math.round(value * 100)}%`}
                onChange={(card_opacity) => update({ card_opacity })}
              />
            )}
            <SwitchRow
              ui={ui}
              label={t("unitDisplay.statusDot", "Punto de estado")}
              checked={settings.status_dot}
              onChange={(status_dot) => update({ status_dot })}
            />
            <SwitchRow
              ui={ui}
              label={t("unitDisplay.showSpeed", "Mostrar velocidad")}
              checked={settings.show_speed}
              onChange={(show_speed) => update({ show_speed })}
            />
            {settings.show_speed && (
              <SliderRow
                ui={ui}
                label={t("unitDisplay.speedSize", "Tamaño de la velocidad")}
                field="speed_px"
                value={settings.speed_px}
                format={(value) => `${value} px`}
                onChange={(speed_px) => update({ speed_px })}
              />
            )}
          </Section>

          <Section ui={ui} icon="ri-truck-line" title={t("unitDisplay.sectionIcon", "Icono")}>
            <SliderRow
              ui={ui}
              label={t("unitDisplay.iconSize", "Tamaño del icono")}
              field="icon_scale"
              value={settings.icon_scale}
              format={(value) => `×${value.toFixed(2).replace(/\.?0+$/, "")}`}
              onChange={(icon_scale) => update({ icon_scale })}
            />
          </Section>

          <div className={`flex items-center justify-between border-t pt-2.5 ${ui.divider}`}>
            <span className={`flex items-center gap-1 text-[10px] ${ui.muted}`}>
              <i className="ri-save-line" aria-hidden />
              {t("unitDisplay.saved", "Se guarda en este navegador")}
            </span>
            <button
              type="button"
              onClick={reset}
              disabled={isDefaultUnitDisplay(settings)}
              className={`rounded-md px-2 py-1 text-[11px] font-medium disabled:opacity-40 disabled:hover:bg-transparent ${ui.ghost}`}
            >
              {t("unitDisplay.reset", "Restablecer")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
